import React, { useCallback, useState } from "react"
import types from "prop-types"
import CSVReader from "react-csv-reader"
import {
  isNotBlankString, noExcelMetacharacters, noHTML,
} from "utils/customFormsyValidations"
import { squish } from "utils/stringHelpers"
import { useModal } from "hooks"
import { CustomIconButton, PrimaryButton, TertiaryButton } from "shared/buttons"
import Modal, { ModalHeader } from "shared/Modal"
// eslint-disable-next-line max-len
import SelectCSVUploadOverview from "FormBuilder/QuestionFields/QuestionTypeAttributes/SelectCSVAttributes/SelectCSVUploadOverview"

const CSV_READER_PARSER_OPTIONS = {
  header: false,
  dynamicTyping: false,
  skipEmptyLines: true,
}

const ImportAnswerOptionsListFromCSVButton = ({ onImport }) => {
  const [uploadDetails, setUploadDetails] = useState()
  const { modalIsOpen, openModal, closeModal } = useModal()

  const storeUploadDetails = useCallback((data, fileInfo) => {
    setUploadDetails({
      filename: fileInfo.name,
      options: data.map((row) => squish(row[0])),
    })
  }, [])

  const close = useCallback(() => {
    setUploadDetails(null)
    closeModal()
  }, [closeModal])

  const commitUpload = useCallback(() => {
    onImport(uploadDetails.options)
    close()
  }, [close, onImport, uploadDetails])

  const { filename, options = [] } = uploadDetails || {}

  const invalidOverview = uploadDetails
    ? (() => {
      const canonize = (value) => squish(value)?.toLowerCase()
      const canonizedCounts = options.reduce((counts, option) => {
        const key = canonize(option)
        counts.set(key, (counts.get(key) || 0) + 1)
        return counts
      }, new Map())

      return options.reduce((list, option) => {
        const checks = [
          isNotBlankString(null, option),
          noExcelMetacharacters(null, option),
          noHTML(null, option),
          canonizedCounts.get(canonize(option)) > 1 ? "Cannot repeat options in list" : true,
        ]

        const errorMessages = checks.filter((result) => result !== true)

        if (errorMessages.length > 0) {
          list.push({ option, errorMessages })
        }

        return list
      }, [])
    })()
    : []

  const allValidOptions = uploadDetails && invalidOverview.length === 0

  return (
    <>
      <CustomIconButton
        text="Import From CSV"
        className="tertiary-button"
        icon="import-csv"
        onClick={openModal}
      />
      <Modal isOpen={modalIsOpen}>
        <ModalHeader
          closeModal={close}
          heading="Upload CSV with Options"
        />
        <div className="mb-6">
          {
            uploadDetails
              ? <SelectCSVUploadOverview filename={filename} invalidOverview={invalidOverview} options={options} />
              : <CSVReader onFileLoaded={storeUploadDetails} parserOptions={CSV_READER_PARSER_OPTIONS} />
          }
        </div>
        <div className="mb-4 text-xs text-dark">Confirming will replace all current options.</div>
        <div className="flex justify-center">
          <TertiaryButton className="mr-2" onClick={close} text="Cancel" />
          {
            allValidOptions && (
              <PrimaryButton onClick={commitUpload} text="Confirm Options" />
            )
          }
        </div>
      </Modal>
    </>
  )
}

ImportAnswerOptionsListFromCSVButton.propTypes = {
  onImport: types.func.isRequired,
}

export default ImportAnswerOptionsListFromCSVButton
